import '@atlaskit/css-reset';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { envType } from '@atlassiansox/analytics-web-client';
import { analyticsClient, initializeAnalyticsClient } from '@databases/analytics';
import { getCurrentSpace, getCurrentUserId, getWindow } from '@databases/connect';
import { fetchData, getCloudId } from '@databases/database-state';
import {
  getFeatureFlagsInUse,
  initializeFeatureFlagClient,
} from '@databases/feature-flags/client';
import { loadConfluenceFlags } from '@databases/feature-flags/confluenceFlags';
import { processQueryParamFeatureFlags } from '@databases/feature-flags/featureFlagUrlOverrides';
import { setupGlobalLoadExperienceTimings } from '@databases/frontend-metrics/timings';
import { IntlProviderWithMessages, useLocale } from '@databases/intl';
import { setBatchAdapter } from '@databases/utils/redux/redux';
import {
  logError,
  checkAppMode,
  logFatalError,
  serializeError,
} from '@databases/utils/utils';
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { batch } from 'react-redux';

import { currentUserState } from '@canvas/canvas-state';
import { getIdentity, synchroniseIdentity } from '@canvas/connection/identity';

import ConnectFatalError from './common/components/ConnectFatalError';
import './store-tracking';
import './dev';
import { initSentry } from './sentry';

(async () => {
  document.documentElement.classList.add('orderly-initialized');

  initSentry();

  try {
    const window = getWindow();
    checkAppMode();
    processQueryParamFeatureFlags(window.location.href);
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch {
    /* empty */
  }

  const cloudId = getCloudId();
  const currentUserId = await getCurrentUserId();

  Sentry.getCurrentScope().setTag(
    'cloudId',
    ENVTYPE === envType.LOCAL || !ENVTYPE ? 'local' : cloudId
  );

  if (currentUserId) {
    Sentry.setUser({ id: currentUserId });
  }

  await synchroniseIdentity();
  await getIdentity()
    .then((data) => {
      currentUserState.status = 'succeeded';
      currentUserState.identity = data;
    })
    .catch((err: unknown) => {
      if (err instanceof Error) {
        logError(err);
      }
    });

  const spaceKey = (await getCurrentSpace()).key;
  let response;
  let spaceId: string | undefined;
  try {
    response = await fetchData(`/rest/api/space/${encodeURIComponent(spaceKey)}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });
    spaceId = (await response.json())?.id;
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (e: any) {
    logError(`Failed to fetch space id`, e);
  }

  await initializeAnalyticsClient(
    ENVTYPE === envType.LOCAL || !ENVTYPE ? undefined : cloudId,
    spaceId,
    currentUserId,
    ENVTYPE ?? envType.LOCAL,
    SENTRY_RELEASE
  );

  setupGlobalLoadExperienceTimings();
  await GlobalPageLoadExperience.startPageLoad('pending', true, performance.now());

  const featureFlagClient = await initializeFeatureFlagClient(
    analyticsClient,
    BUILD_MODE,
    cloudId,
    currentUserId
  );

  await loadConfluenceFlags();

  try {
    const featureFlagsInUse = getFeatureFlagsInUse(featureFlagClient.getFlags());
    if (featureFlagsInUse.length > 0) {
      Sentry.setContext('Feature Flags', {
        'In Use': featureFlagsInUse,
      });
    }
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (e) {
    console.error('Failed to add feature flag tags to Sentry');
    console.error(e);
  }

  setBatchAdapter(batch);

  GlobalPageLoadExperience.mark('load-app-start');
  const module = await import('./apps/databases-overview-page');
  GlobalPageLoadExperience.mark('load-app-end');

  GlobalPageLoadExperience.mark('mount-app-start');
  await module.default();
  GlobalPageLoadExperience.mark('mount-app-end');

  document.documentElement.classList.add('orderly-loaded');
})().catch((error: unknown) => {
  let formattedError = undefined;
  if (error instanceof Error) {
    logFatalError(error);
    formattedError = serializeError(error);
    formattedError = {
      name: formattedError.name,
      message: formattedError.message,
      stack: formattedError.stack,
      code: formattedError.code,
    };

    GlobalPageLoadExperience.failure({
      metadata: { error: formattedError },
      force: true,
    });

    try {
      createRoot(document.getElementById('app')!).render(
        <IntlProviderWithMessages locale={useLocale()}>
          <ConnectFatalError error={error} />
        </IntlProviderWithMessages>
      );

      document.documentElement.classList.add('orderly-loaded');
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch (error2) {
      if (error2 instanceof Error) {
        logFatalError(error2);

        document.getElementById('global-error')!.innerText = error.message;
        document.getElementById('global-error')!.style.display = '';
        document.getElementById('app')!.style.display = 'none';
        document.documentElement.classList.add('orderly-loaded');
      }
    }
  }
});
